import type { CanvasserStatsItem } from '@signalvote/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { usePaidVsVolunteerComparison } from '../../hooks/queries';
import type { GroupStats } from '../../hooks/queries';
import { getReliabilityColor, getSurveyColor } from './colorUtils';

type MetricKey = 'avgDoorsPerHour' | 'avgReachRate' | 'avgSurveyCompletion' | 'avgReliability' | 'count' | 'totalHours';

interface MetricDef {
  key: MetricKey;
  label: string;
  format: (value: number) => string;
  getColor?: (value: number) => string;
}

const METRICS: MetricDef[] = [
  { key: 'count', label: 'Canvassers', format: (v) => String(v) },
  { key: 'totalHours', label: 'Total Hours', format: (v) => v.toFixed(1) },
  { key: 'avgDoorsPerHour', label: 'Avg Doors/Hr', format: (v) => v.toFixed(1) },
  { key: 'avgReachRate', label: 'Avg Reach %', format: (v) => `${v.toFixed(1)}%` },
  { key: 'avgSurveyCompletion', label: 'Avg Survey %', format: (v) => `${v.toFixed(1)}%`, getColor: getSurveyColor },
  { key: 'avgReliability', label: 'Avg Reliability', format: (v) => `${v.toFixed(1)}%`, getColor: getReliabilityColor },
];

function getMetricValue(stats: GroupStats, key: MetricKey): number | null {
  return stats[key];
}

function formatSubtext(
  directHire: GroupStats | null,
  vendorManaged: GroupStats | null,
  metric: MetricDef
): string | null {
  if (!directHire || !vendorManaged) return null;
  const dh = getMetricValue(directHire, metric.key);
  const vm = getMetricValue(vendorManaged, metric.key);
  if (dh === null || vm === null) return null;
  return `(${metric.format(dh)} direct-hire, ${metric.format(vm)} vendor-managed)`;
}

interface PaidVsVolunteerSectionProps {
  canvasserStats: CanvasserStatsItem[];
}

export function PaidVsVolunteerSection({ canvasserStats }: PaidVsVolunteerSectionProps) {
  const comparison = usePaidVsVolunteerComparison(canvasserStats);

  if (!comparison) return null;

  const { volunteer, paid, directHire, vendorManaged } = comparison;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <GroupCard
        title="Volunteer"
        stats={volunteer}
        otherStats={paid}
        prefix="vol"
        directHire={null}
        vendorManaged={null}
      />
      <GroupCard
        title="Paid"
        stats={paid}
        otherStats={volunteer}
        prefix="paid"
        directHire={directHire}
        vendorManaged={vendorManaged}
      />
    </div>
  );
}

interface GroupCardProps {
  title: string;
  stats: GroupStats;
  otherStats: GroupStats;
  prefix: 'vol' | 'paid';
  directHire: GroupStats | null;
  vendorManaged: GroupStats | null;
}

function GroupCard({ title, stats, otherStats, prefix, directHire, vendorManaged }: GroupCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {METRICS.map((metric) => {
            const value = getMetricValue(stats, metric.key);
            const otherValue = getMetricValue(otherStats, metric.key);
            const isHigher = value !== null && otherValue !== null && value > otherValue;

            // Use semantic color for reliability/survey, comparative green for other metrics
            let colorClass = '';
            if (value !== null && metric.getColor) {
              colorClass = metric.getColor(value);
            } else if (isHigher) {
              colorClass = 'text-green-600';
            }

            const subtext = prefix === 'paid' ? formatSubtext(directHire, vendorManaged, metric) : null;

            return (
              <div key={metric.key}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{metric.label}</span>
                  <span
                    className={`text-sm font-medium tabular-nums ${colorClass}`}
                    data-testid={`${prefix}-${metric.key}`}
                  >
                    {value !== null ? metric.format(value) : 'N/A'}
                  </span>
                </div>
                {subtext && (
                  <p
                    className="text-right text-xs text-muted-foreground"
                    data-testid={`${prefix}-subtext-${metric.key}`}
                  >
                    {subtext}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
